import { IconShieldFilled } from "@tabler/icons-react"
import { type FC, type ReactNode, useState } from "react"
import styled from "styled-components"

import {
	type CommunityPostComment,
	type CommunityPostReactionType,
	communityPostReactionTypes,
} from "@forento/core/models/community"
import { toDateTimeString } from "@forento/core/utilities/date"
import CommunityPostReactionPicker from "@forento/ui/components/CommunityPostReactionPicker"
import CommunityPostReactionSummary from "@forento/ui/components/CommunityPostReactionSummary"
import CommunityProfileAvatar from "@forento/ui/components/CommunityProfileAvatar"

import CommentReactions from "~/components/CommentReactions"
import { DeleteIcon, LikeIcon } from "~/components/Icon"
import { toRelativeDateString } from "~/utilities/date"

type Props = {
	comment: CommunityPostComment
	canReact: boolean
	replyAction?: ReactNode
	replyContext?: ReactNode
	onReact(type: CommunityPostReactionType | null): void
	onDelete?(): void
}
const Comment: FC<Props> = ({ comment, canReact, onReact, onDelete, replyAction, replyContext }) => {
	const [isReactionsModalOpen, setReactionsModalOpen] = useState(false)

	const reactionCount = communityPostReactionTypes.reduce((sum, type) => sum + comment.reactionCounts[type], 0)

	return (
		<Container key={comment.id}>
			<CommentReactions
				commentId={isReactionsModalOpen ? comment.id : null}
				onClose={() => setReactionsModalOpen(false)}
			/>
			<CommunityProfileAvatar profile={comment.profile} size={32} backgroundColor="white" />
			<Content>
				<Header>
					<AuthorName>
						{comment.profile.firstName} {comment.profile.lastName}
						{comment.profile.isCreator && (
							<IconShieldFilled className="size-4 text-indigo-500" title="Admin" />
						)}
					</AuthorName>
					{onDelete && (
						<button
							type="button"
							onClick={onDelete}
							className="cursor-pointer text-red-400 hover:text-red-600"
							aria-label="Delete comment"
						>
							<DeleteIcon className="size-4" />
						</button>
					)}
				</Header>
				{replyContext}
				<Text dangerouslySetInnerHTML={{ __html: comment.htmlContent }} />
				<div className="flex items-center gap-3">
					<DateText title={toDateTimeString(comment.createDate)}>
						{toRelativeDateString(comment.createDate)}
					</DateText>
					{canReact && (
						<CommunityPostReactionPicker
							value={comment.userReactionType}
							onSelect={onReact}
							className={
								comment.userReactionType !== null ? "text-primary text-xs" : "text-xs text-gray-500"
							}
						>
							<LikeIcon className="size-4" />
						</CommunityPostReactionPicker>
					)}
					{reactionCount > 0 && (
						<button
							type="button"
							onClick={() => setReactionsModalOpen(true)}
							className="cursor-pointer text-xs"
						>
							<CommunityPostReactionSummary counts={comment.reactionCounts} size="small" />
						</button>
					)}
					{replyAction}
				</div>
			</Content>
		</Container>
	)
}

const Container = styled.div`
	display: flex;
	gap: 6px;
`

const Content = styled.div`
	width: 100%;
	background-color: white;
	border-radius: 8px;
	padding: 6px 10px;
`

const Header = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 2px;
`

const AuthorName = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	font-size: 12px;
`

const Text = styled.div`
	a {
		color: #007bff;
	}
`

const DateText = styled.div`
	color: #949494;
	font-size: 12px;
`

export default Comment
